// src/services/albumExtensionService.js
// Service for album extension functionality (website version)

import supabase from './supabase';
import axios from 'axios';

const CLOUD_FUNCTIONS_URL = process.env.REACT_APP_CLOUD_FUNCTIONS_URL ||
  'https://asia-south1-invits-app.cloudfunctions.net';

/**
 * Get authorization header for API calls
 */
const getAuthHeader = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('Not authenticated');
  }
  return {
    'Authorization': `Bearer ${session.access_token}`,
    'Content-Type': 'application/json',
  };
};

/**
 * Get albums linked to an event
 */
export const getEventLinkedAlbums = async (eventId) => {
  try {
    const { data, error } = await supabase
      .from('creator_albums')
      .select(`
        *,
        creator:creator_id (
          id,
          email
        )
      `)
      .eq('event_id', eventId)
      .eq('link_status', 'approved');

    if (error) throw error;

    return {
      success: true,
      albums: data || [],
    };
  } catch (error) {
    console.error('Error fetching linked albums:', error);
    return {
      success: false,
      error: error.message,
      albums: [],
    };
  }
};

/**
 * Get album extension pricing tiers
 */
export const getAlbumExtensionPricing = async () => {
  try {
    const { data, error } = await supabase
      .from('album_extension_pricing')
      .select('*')
      .eq('is_active', true)
      .order('duration_months', { ascending: true });

    if (error) throw error;

    return {
      success: true,
      pricing: data || [],
    };
  } catch (error) {
    console.error('Error fetching album extension pricing:', error);
    return {
      success: false,
      error: error.message,
      pricing: [],
    };
  }
};

/**
 * Calculate extension price based on album size and duration
 */
export const calculateExtensionPrice = (sizeBytes, pricingTier) => {
  const sizeGB = Math.ceil(sizeBytes / (1024 * 1024 * 1024));
  const pricePerGB = pricingTier.price_per_gb;
  const durationMultiplier = pricingTier.duration_multiplier;

  const baseAmount = sizeGB * pricePerGB * durationMultiplier;
  const gstRate = 18;
  const gstAmount = parseFloat(((baseAmount * gstRate) / 100).toFixed(2));
  const totalAmount = parseFloat((baseAmount + gstAmount).toFixed(2));

  return {
    albumSizeGB: sizeGB,
    pricePerGB,
    durationMonths: pricingTier.duration_months,
    durationMultiplier,
    baseAmount,
    gstAmount,
    totalAmount,
    gstRate,
  };
};

/**
 * Create album extension order
 */
export const createAlbumExtensionOrder = async (params) => {
  try {
    const {
      albumId,
      durationMonths,
      customerName,
      customerEmail,
      customerPhone,
      customerState,
    } = params;

    if (!albumId || !durationMonths || !customerName || !customerEmail || !customerPhone) {
      throw new Error('Missing required fields');
    }

    const headers = await getAuthHeader();

    const response = await axios.post(
      `${CLOUD_FUNCTIONS_URL}/createAlbumExtensionOrderCashfree`,
      {
        albumId,
        durationMonths,
        customerName,
        customerEmail,
        customerPhone,
        customerState,
      },
      { headers }
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to create order');
    }

    return {
      success: true,
      orderId: response.data.orderId,
      purchaseId: response.data.purchaseId,
      paymentSessionId: response.data.paymentSessionId,
      baseAmount: response.data.baseAmount,
      gstAmount: response.data.gstAmount,
      totalAmount: response.data.totalAmount,
    };
  } catch (error) {
    console.error('Error creating album extension order:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to create order',
    };
  }
};

/**
 * Verify album extension payment
 */
export const verifyAlbumExtensionPayment = async (orderId) => {
  try {
    if (!orderId) {
      throw new Error('Order ID is required');
    }

    const headers = await getAuthHeader();

    const response = await axios.post(
      `${CLOUD_FUNCTIONS_URL}/verifyAlbumExtensionPaymentCashfree`,
      { orderId },
      { headers }
    );

    if (!response.data.success) {
      return {
        success: false,
        paymentStatus: response.data.paymentStatus || 'failed',
        error: response.data.error || 'Payment verification failed',
      };
    }

    return {
      success: true,
      paymentStatus: response.data.paymentStatus,
      purchaseId: response.data.purchaseId,
      albumId: response.data.albumId,
      albumName: response.data.albumName,
      invoiceId: response.data.invoiceId,
      invoiceNumber: response.data.invoiceNumber,
      newExpiry: response.data.newExpiry,
    };
  } catch (error) {
    console.error('Error verifying album extension payment:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Payment verification failed',
      paymentStatus: 'error',
    };
  }
};

/**
 * Format storage size for display
 */
export const formatStorageSize = (bytes) => {
  if (!bytes) return '0 B';
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
};

/**
 * Format date for display
 */
export const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Get expiry status for an album
 */
export const getExpiryStatus = (expiryDate) => {
  if (!expiryDate) {
    return {
      status: 'active',
      label: 'Active',
      daysLeft: null,
    };
  }

  const now = new Date();
  const expiry = new Date(expiryDate);
  const daysLeft = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));

  if (daysLeft < 0) {
    return {
      status: 'expired',
      label: 'Expired',
      daysLeft: 0,
    };
  }

  if (daysLeft <= 3) {
    return {
      status: 'critical',
      label: `${daysLeft} days left`,
      daysLeft,
    };
  }

  if (daysLeft <= 7) {
    return {
      status: 'warning',
      label: `${daysLeft} days left`,
      daysLeft,
    };
  }

  if (daysLeft <= 30) {
    return {
      status: 'soon',
      label: `${daysLeft} days left`,
      daysLeft,
    };
  }

  return {
    status: 'active',
    label: formatDate(expiryDate),
    daysLeft,
  };
};
